import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntersection } from "react-use";

import PlayCircleIcon from "public/icons/play_circle.svg";

import { CardActionArea, css, styled } from "@mui/material";

import { usePayVideo } from "@/api/userRpc/video/hook/usePayVideo";
import { PayVideoResponseDTO } from "@/api/userRpc/video/models/PayVideo.responce.dto";
import { useLadyIdContext } from "@/context/LadyId.context";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { AuthRenderer } from "@/context/auth-renderer/AuthRenderer";
import { useFavorite } from "@/features/lady-profile/hooks";
import { NewsfeedToggleMediaFavorite } from "@/features/newsfeed/hooks";
import { LadyMinV2DTO } from "@/models/lady-dtos";
import { WithClassName } from "@/models/utils.models";
import { clsxm } from "@/utils/tailwindMerge";

import { useOpenModal } from "../modal/utils/openModal";
import { FavoriteButton } from "../ui/favorite-button/FavoriteButton";
import { SvgIcon } from "../ui/svg-icon/SvgIcon";

type LadyVideoProps = {
  skipFavoriteButton?: boolean;
  videoObj: {
    static: string;
    gif: string;
    isFavorite?: boolean;
    id?: number;
    isPaid?: boolean;
    fileNameUrl?: string;
  };
  ladyProfileDto?: LadyMinV2DTO;
  sliderData?: {
    firstName: string;
    from: string;
    linkToLadyProfile: string;
    url: string;
    isOnline: boolean;
    id: number;
  } | null;
  index?: number;
  onFavoriteClick?: NewsfeedToggleMediaFavorite;
};
type CardProps = {
  imageLink?: string;
};

const DivImg = styled("div")<CardProps>(({ imageLink }) => {
  return imageLink
    ? css`
        background-image: url("${imageLink}");
      `
    : css``;
});

export const LadyVideo: React.FC<LadyVideoProps & WithClassName> = ({
  videoObj,
  skipFavoriteButton,
  className,
  ladyProfileDto,
  sliderData,
  index,
  onFavoriteClick,
}) => {
  const { openModal, removeOne } = useOpenModal();
  const { openModal: disableBGClickModal } = useOpenModal({
    disableBackGroundClick: true,
  });
  const { onFavouriteClick } = useFavorite({
    objectId: videoObj.id,
    isFavorite: videoObj.isFavorite,
    favoriteType: "VIDEO",
    ignoreLocalState: true,
  });

  const cardActionAreaRef = useRef<HTMLButtonElement>(null);
  const onceLoaded = useRef(false);
  const intersectOptions = useIntersection(cardActionAreaRef, {
    root: null,
    rootMargin: "0px",
    threshold: 0,
  });
  const { setLadyId } = useLadyIdContext();
  const authContext = useAuthContext();
  const openModalWithHeader = useCallback(
    (header: string) => () => {
      ladyProfileDto && setLadyId(ladyProfileDto.id.toString());

      openModal("PROFILE_ACTION_BUTTONS_MODAL", {
        onClose: removeOne,
        header,
        ladyDtoParam: ladyProfileDto,
      });
    },
    [ladyProfileDto, openModal, removeOne, setLadyId],
  );

  const [isDisplaying, setIsDisplaying] = useState(false);

  useEffect(() => {
    if (intersectOptions?.isIntersecting) {
      setIsDisplaying(true);
    }

    return () => {
      onceLoaded.current = false;
    };
  }, [intersectOptions?.isIntersecting]);
  const openVideo = (response: PayVideoResponseDTO) => {
    if (videoObj.id) {
      const ladyData = ladyProfileDto || sliderData;
      const url = ladyProfileDto
        ? ladyProfileDto.photoSrcs.small
        : ladyData?.url;
      if (!ladyData) {
        console.error("No lady data available");
        return;
      }

      openDarkModal("LADY_PROFILE_SLIDER_MODAL", {
        isBottomButtons: true,
        bottomButtonsProps: {
          firstName: ladyData.firstName,
          from: ladyData.from,
          linkToLadyProfile: `/lady/id${ladyData.id}`,
          avatarSrc: url || "",
          isOnline: ladyData.isOnline || false,
        },
        onClose: removeOne,
        initialIndex: 0,
        media:
          response?.requestDTO?.dataMap &&
          "result" in response.requestDTO.dataMap
            ? [
                {
                  type: "video",
                  source: response.requestDTO.dataMap.result?.fileNameUrl,
                  id: videoObj.id,
                },
              ]
            : [
                {
                  type: "intro_video",
                  source: videoObj.static,
                  id: videoObj.id,
                },
              ],
        alt: `Video of ${ladyData.firstName || "lady"}`,
      });
    }
  };
  const { mutate: payForVideo } = usePayVideo({ openVideo });

  const openConfirmModal = (message: string) => {
    if (videoObj.id) {
      const ladyData = ladyProfileDto || sliderData;

      if (!ladyData) {
        console.error("No lady data available");
        return;
      }

      disableBGClickModal("INFORMATION_MODAL", {
        message,
        statusReg: "success",
        buttonText: "Ok",
        headerName: "Confirm",
        disableonOkClickOnCross: true,
        onOkClick: () => {
          if (videoObj.id !== undefined) {
            payForVideo({ videoId: videoObj.id, ladyId: ladyData.id });
          }
          removeOne();
        },
      });
    }
  };

  const { openModal: openDarkModal } = useOpenModal({
    disableScrollActions: true,
    darkerBackdrop: true,
  });

  const handleVideoClick = () => {
    if (authContext.isAuthenticated) {
      if (!videoObj.isPaid) {
        openConfirmModal(
          "10 coins will be deducted from your account for viewing unwatched video",
        );
      }
    } else {
      openModalWithHeader("watch videos")();
    }
  };

  return (
    <div data-testid="ladyVideo" className={clsxm("relative", className)}>
      <CardActionArea
        className="rounded-[17px] overflow-clip h-full text-white"
        onClick={handleVideoClick}
        ref={cardActionAreaRef}
        aria-label={`${videoObj.isPaid ? "Open" : "Buy"} video`}
      >
        {" "}
        <DivImg
          imageLink={isDisplaying ? videoObj.static : undefined}
          className="h-0 pt-[66%] bg-cover bg-center"
        ></DivImg>
        <DivImg
          imageLink={isDisplaying ? videoObj.gif : undefined}
          className={clsxm(
            "h-0 pt-[66%] bg-cover bg-center",
            "absolute top-0 left-0 right-0 bottom-0 opacity-0 transition-opacity duration-500",
            "hover:opacity-100",
          )}
        ></DivImg>
        {!videoObj.isPaid ? (
          <div
            data-testid="videoBuyButton"
            className={clsxm(
              "flex flex-col py-1 px-4 rounded-full gap-0 lowercase bg-grey1-900 bg-opacity-50 text-center",
              skipFavoriteButton
                ? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                : "absolute bottom-4 left-1/2 -translate-x-1/2",
            )}
          >
            <p className="text-[11px] font-bold normal-case">
              Watch this video
            </p>
            <p className="text-[10px] font-bold capitalize">10 COINS</p>
          </div>
        ) : (
          <div
            className={clsxm(
              "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none max-w-[52px]",
              "bg-grey1-800 bg-opacity-30 rounded-full",
            )}
          >
            <SvgIcon className="w-full h-full">
              <PlayCircleIcon />
            </SvgIcon>
          </div>
        )}
        <AuthRenderer>
          <AuthRenderer.IsAuth>
            {skipFavoriteButton ? null : (
              <FavoriteButton
                isFavorite={videoObj?.isFavorite}
                onClick={(e) => {
                  e.stopPropagation();
                  onFavoriteClick?.({
                    sectionInd: index || 0,
                    objectId: videoObj.id || 0,
                    mediaType: "video",
                  });
                  onFavouriteClick(e);
                }}
                ariaLabel={
                  videoObj.isFavorite
                    ? "Remove video from favorite"
                    : "Add video to favorite"
                }
              />
            )}
          </AuthRenderer.IsAuth>
        </AuthRenderer>
      </CardActionArea>
    </div>
  );
};
